import { contains, type Singleton } from './common';
import type { Client, RequestOptions } from '../generated/client/types.gen';
import { jwt } from './jwt';
import { refresh } from './refresh';
import { ErrorCode401Enum } from '../generated/types.gen';
import type { ApiError } from '../errors';

/**
 * Intercept and process an API response.
 * @param response The original `Response` object.
 * @param request The original `Request` object.
 * @param options The request options.
 * @returns A `Promise` that resolves to a `Response`.
 */
export type ResponseInterceptor = (response: Response, request: Request, options: RequestOptions) => Promise<Response>;

/**
 * Configure the reactive response interceptor.
 */
export type ResponseInterceptorOptions = {
  /** The token manager singleton. */
  token?: Singleton<string>;
  /** Execute when token refresh fails. */
  on_refresh_failed?: (error?: ApiError) => void;
  /** A list of URLs to ignore for token refresh logic. */
  ignored_urls?: string[];
};

/**
 * Create a response interceptor to reactively refresh an expired JWT upon a 401 Unauthorized response.
 *
 * Act as a fallback mechanism. If an API call fails with a 401 status,
 * check if the token is refreshable. If so, attempt to get a new token and
 * automatically retry the original request.
 *
 * @param options Configure the interceptor, including the token manager and callbacks.
 * @returns A function that registers the interceptor on a client instance and returns its ID.
 */
export const response =
  (options: ResponseInterceptorOptions) =>
  (client: Client): number => {
    const interceptor: ResponseInterceptor = async (res, req, _) => {
      const { token, on_refresh_failed, ignored_urls = [] } = options;

      if (res.status !== 401 || !token || contains(req.url, ignored_urls)) {
        return res;
      }

      const current = token.get();
      if (!current || !jwt.can_refresh(current)) {
        on_refresh_failed?.({
          type: ErrorCode401Enum.NOT_AUTHENTICATED,
          errors: [{ code: 'token_not_refreshable', detail: 'Token cannot be refreshed.', attr: null }],
        });

        return res;
      }

      const refresh_result = await refresh({ token, client, on_refresh_failed });

      if (refresh_result?.token) {
        const cloned_request = req.clone();
        cloned_request.headers.set('Authorization', `Bearer ${refresh_result.token}`);

        return fetch(cloned_request);
      }

      return res;
    };

    return client.interceptors.response.use(interceptor);
  };
